import React from 'react';
import { Box, Typography, Divider, useTheme } from '@mui/material';

interface CarouselCardMAIQProps {
  content: string; // Main content with markdown-like syntax
  onClick?: () => void;
  height?: number;
}

const CarouselCardMAIQ: React.FC<CarouselCardMAIQProps> = ({ 
  content,
  onClick,
  height = 200
}) => {
  const theme = useTheme();

  const handleMouseMove = (event: React.MouseEvent) => {
    // Extract tooltip content from the markdown
    const tooltipMatch = content.match(/##tooltip::([^#]+)##/);
    if (tooltipMatch) {
      // For now, just log tooltip - can be enhanced later
      console.log('Tooltip:', tooltipMatch[1]);
    }
  };

  // Parse content and render appropriate components
  const renderContent = () => {
    const sections = content.split('##').filter(Boolean);
    return sections.map((section, index) => {
      const [type, ...contentParts] = section.trim().split('::');
      const content = contentParts.join('::').trim();

      // Skip tooltip section as it's handled separately
      if (type.toLowerCase() === 'tooltip') return null;

      switch (type.toLowerCase()) {
        case 'title':
          return (
            <Typography
              key={index}
              variant="h4"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 700,
                mb: 1,
                textAlign: 'center',
                fontSize: { xs: '1.5rem', md: '2rem' },
                lineHeight: 1.1,
              }}
            >
              {content}
            </Typography>
          );
        case 'subtitle':
          return (
            <Typography
              key={index}
              variant="subtitle1"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 600,
                mb: 0.5,
                textAlign: 'center',
                fontSize: { xs: '1rem', md: '1.15rem' },
              }}
            >
              {content}
            </Typography>
          );
        case 'text':
          return (
            <Typography
              key={index}
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                textAlign: 'center',
                fontSize: { xs: '0.95rem', md: '1rem' },
              }}
            >
              {content}
            </Typography>
          );
        case 'divider':
          return <Divider key={index} sx={{ my: 1, width: '80%' }} />;
        default:
          return null;
      }
    });
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: height,
        background: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[3],
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        position: 'relative',
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          boxShadow: onClick ? theme.shadows[4] : undefined,
          transform: onClick ? 'translateY(-2px)' : undefined,
        },
      }}
      onClick={onClick}
      onMouseMove={handleMouseMove}
    >
      {renderContent()}
    </Box>
  );
};

export default CarouselCardMAIQ;
